import {TweenMax, Power4} from 'gsap';
import Draggable from 'gsap/Draggable';
import ThrowPropsPlugin from '../../libraries/gsap-bonus/ThrowPropsPlugin';
import {Emitter, emitter, EVENTS} from '../../utils/emitter';
import {CarouselItem} from './carouselItem';
import {device} from '../../utils/device';
import {clamp} from '../../utils/math/clamp';
import {partial} from '../../utils/partial';
import Mustache from 'mustache';
import {Window} from '../../utils/controllers/window';
import carouselItemTemplate from '../../views/pages/home/carouselItem.html';

export class Carousel extends Emitter {
  public element: HTMLElement;
  public size: any;
  public carouselItemContainer: HTMLElement;
  private descriptionsTicker: HTMLElement;
  private descriptionScroller: HTMLElement;
  private carouselDockedInfo: HTMLElement;
  private readMore: HTMLElement;
  private carouselItems: Array<CarouselItem> = [];
  private totalItems: number = 0;
  private draggables: Array<Draggable>;
  private zoomed: boolean;
  private dragEnabled: boolean = false;
  private scrollEnabled: boolean = true;
  private currentIndex: number = 0;
  private bounds: any;
  private cursors: HTMLElement;
  private mouseMoveEvent: Event;

  constructor(
    element: HTMLElement,
    carouselItemData: Array<any>,
    carouselDockedInfo: HTMLElement,
    cursors: HTMLElement
  ) {
    super();

    // Prevent webpack tree shaking
    ThrowPropsPlugin;

    this.size = {
      aspectRatio: 1.77,
      marginTop: 0,
      marginLeft: 0,
      minPadding: 0,
      boundsTop: 100,
      boundsBottom: 190,
      boundsLeft: 0,
      maxSlideWidth: 1400,
      maxSlideHeight: 800
    };

    this.carouselDockedInfo = carouselDockedInfo;

    this.element = element;
    this.carouselItemContainer = element.querySelector('.items');

    // Parse entry data into carousel item templates
    for (let i = 0; i < carouselItemData.length; i++) {
      let itemElement = partial(Mustache.render(carouselItemTemplate, carouselItemData[i]));
      this.carouselItemContainer.appendChild(itemElement);
      const item = new CarouselItem(itemElement);
      item.on(EVENTS.select, this.onItemSelectHandler);
      item.on(EVENTS.mouseOver, this.onItemMouseOverHandler);
      item.on(EVENTS.mouseOut, this.onItemMouseOutHandler);
      item.index = i;
      this.carouselItems.push(item);
    }

    this.descriptionsTicker = carouselDockedInfo.querySelector('.descriptions');
    carouselDockedInfo.querySelectorAll('.description')[0].classList.add('active');

    this.setCurrentItem(0);

    this.descriptionScroller = carouselDockedInfo.querySelector('.scroller');
    this.readMore = carouselDockedInfo.querySelector('.readMore');
    this.cursors = cursors;

    this.totalItems = this.carouselItems.length;
    this.presetAnimation();
  }

  public init() {
    this.draggables = Draggable.create(this.carouselItemContainer, {
      type: 'x',
      edgeResistance: 0.8,
      throwProps: true,
      maxDuration: 1.5,
      minDuration: 0.5,
      onDrag: () => {
        this.update3dEffect();
      },
      onThrowUpdate: () => {
        this.update3dEffect();
      },
      snap: {
        x: function(endValue) {
          const snappedValue = this.getSnapValue(endValue);
          this.setCurrentItem(Math.round(-snappedValue / this.getSlideWidth()));
          this.tickDescription();
          this.enableRollovers();
          return snappedValue;
        }.bind(this)
      }
    });

    if (!device.isTouch()) {
      this.disableDrag();
    }

    this.addHandlers();
    this.resize();
  }

  private addHandlers() {
    this.element.addEventListener('mousemove', this.onItemMouseMoveHandler);
    this.readMore.addEventListener('click', this.readMoreHandler);
    emitter.on(EVENTS.resize, this.resize);
    emitter.on(EVENTS.wheel, this.scroll);
  }

  private onItemSelectHandler = e => {
    const itemEl = e.event.target.closest('.carouselItem');
    if (itemEl) {
      const storyId = itemEl.getAttribute('data-storyId');
      if (itemEl.classList.contains('active')) {
        this.emit(EVENTS.select, {storyId: storyId});
      } else {
        const slide = this.getSlideById(storyId);
        if (slide.index < this.currentIndex) {
          this.previous();
        } else {
          this.next();
        }
      }
    }
  };

  private onItemMouseOverHandler = e => {
    if (!this.zoomed && e.event) {
      const arrow: HTMLElement = this.cursors.querySelector('.arrow');
      const readMore: HTMLElement = this.cursors.querySelector('.readMore');

      if (e.item.getActive()) {
        TweenMax.to(readMore.querySelector('svg'), 0.75, {strokeDashoffset: 0});
        readMore.style.display = 'block';
        arrow.style.display = 'none';
      } else {
        readMore.style.display = 'none';
        arrow.style.display = 'block';

        if (e.event.clientX < Window.halfWidth) {
          arrow.classList.add('left');
          e.item.nudgeRight();
        } else {
          arrow.classList.remove('left');
          e.item.nudgeLeft();
        }
      }

      this.showCursors();
    }
  };

  private onItemMouseOutHandler = e => {
    this.hideCursors();
    const readMore: HTMLElement = this.cursors.querySelector('.readMore');
    TweenMax.to(readMore.querySelector('svg'), 0.75, {strokeDashoffset: 290});
    e.item.nudgeBack();
  };

  private onItemMouseMoveHandler = e => {
    this.mouseMoveEvent = e;
    TweenMax.set(this.cursors, {x: e.clientX, y: e.clientY});
  };

  private resize = () => {

    if (Window.width < 600) {
      this.size.boundsLeft = 40;
      this.size.minPadding = 10;
    } else if (Window.width < 900) {
      this.size.boundsLeft = 65;
      this.size.minPadding = 40;
    } else {
      this.size.boundsLeft = 65;
      this.size.minPadding = 80;
    }

    let availableHeight = Window.height - (this.size.boundsTop + this.size.boundsBottom);

    // This will set to the width of the boundry box
    let padding = this.size.minPadding;
    this.size.marginLeft = this.size.boundsLeft + padding;
    let slideWidth = Window.width - this.size.marginLeft * 2 - padding * 2;
    let slideHeight = slideWidth / this.size.aspectRatio;
    this.size.marginTop = (Window.height - slideHeight) / 2 - 40;

    // This will set to the height of the boundry box if fitting to width exceeds the available height or if the width is too wide
    if (slideHeight > availableHeight || slideWidth > this.size.maxSlideWidth) {
      slideHeight = availableHeight;
      if (slideHeight > this.size.maxSlideHeight) {
        slideHeight = this.size.maxSlideHeight;
      }
      this.size.marginTop = (Window.height - slideHeight) / 2 - 40;

      slideWidth = slideHeight * this.size.aspectRatio;
      const fluidPadding = ((Window.width - slideWidth) / 2 - this.size.boundsLeft) / 2;
      if (fluidPadding > this.size.minPadding) {
        padding = fluidPadding;
      }
      this.size.marginLeft = (Window.width - slideWidth - padding * 2) / 2;
    }

    // Update margin left of carousel element.
    // Note: margin top is applied to the imageAndRollover element in carousel item so that
    // it doesn't affect the play button and video player
    TweenMax.set(this.element, {marginLeft: this.size.marginLeft});

    // update slides
    for (var i = 0; i < this.carouselItems.length; i++) {
      this.carouselItems[i].setSize(slideWidth, slideHeight, padding, this.size.marginTop);
    }

    // apply tweenmax draggable bounds
    this.bounds = {minX: 0, maxX: -((this.totalItems - 1) * this.getSlideWidth())};
    this.draggables[0].applyBounds(this.bounds);

    // description ticker
    this.updateDescription(0);

    let dockedInfoTop = this.size.marginTop + slideHeight;

    TweenMax.set(this.carouselDockedInfo, {top: dockedInfoTop});

    // Update position of carousel X
    TweenMax.set(this.carouselItemContainer, {x: -this.currentIndex * this.getSlideWidth()});
  };

  private scroll = ({direction, delta, accelerating}) => {
    if (!this.scrollEnabled) {
      return;
    }

    if (accelerating) {
      if (direction == -1) {
        this.next();
      } else {
        this.previous();
      }
    }
  };

  private disableRollovers() {
    for (var i = 0; i < this.carouselItems.length; i++) {
      this.carouselItems[i].rolloverDisabled = true;
    }
  }

  private enableRollovers() {
    for (var i = 0; i < this.carouselItems.length; i++) {
      this.carouselItems[i].rolloverDisabled = false;
    }
  }

  private update3dEffect() {
    for (var i = 0; i < this.carouselItems.length; i++) {
      this.carouselItems[i].update3dEffect(this.getX());
    }
  }

  private getSlideById(id) {
    for (var i = 0; i < this.carouselItems.length; i++) {
      if (this.carouselItems[i].id == id) {
        return this.carouselItems[i];
      }
    }
  }

  private getSlideWidth() {
    const item = this.carouselItems[0];
    return item.size.width + item.size.padding * 2;
  }

  private setCurrentItem(index) {
    this.carouselItems[this.currentIndex].setActive(false);
    this.carouselItems[index].setActive(true);
    this.carouselItems[index].nudgeBack();
    this.currentIndex = index;
  }

  public getCurrentItem() {
    return this.carouselItems[this.currentIndex];
  }

  public getNextItem() {
    const index = this.currentIndex == this.carouselItems.length - 1 ? 0 : this.currentIndex + 1;
    return this.carouselItems[index];
  }

  public getPreviousItem() {
    const index = this.currentIndex ==  0 ? this.carouselItems.length - 1 : this.currentIndex - 1;
    return this.carouselItems[index];
  }

  private getSnapValue(value) {
    const snappedValue = clamp(
        this.bounds.maxX,
        this.bounds.minX,
        Math.round(value / this.getSlideWidth()) * this.getSlideWidth()
    );
    return Math.round(snappedValue / this.getSlideWidth()) * this.getSlideWidth(); // + this.leftMargin;
  }

  public getX() {
    return this.draggables[0].target._gsTransform.x;
  }

  private moveSideItemsOut(speed) {
    const leftItemIndex = this.currentIndex - 1;
    const rightItemIndex = this.currentIndex + 1;
    if (leftItemIndex > -1) {
      this.carouselItems[leftItemIndex].moveLeft(speed);
    }
    if (rightItemIndex < this.carouselItems.length) {
      this.carouselItems[rightItemIndex].moveRight(speed);
    }
  }

  private moveSideItemsIn(speed) {
    const leftItemIndex = this.currentIndex - 1;
    const rightItemIndex = this.currentIndex + 1;
    if (leftItemIndex > -1) {
      this.carouselItems[leftItemIndex].moveBack(speed);
    }
    if (rightItemIndex < this.carouselItems.length) {
      this.carouselItems[rightItemIndex].moveBack(speed);
    }
  }

  private showDescriptions(speed = 0.4) {
    TweenMax.to(this.carouselDockedInfo, speed, {opacity: 1});
  }

  private hideDescriptions(speed = 0.4) {
    TweenMax.to(this.carouselDockedInfo, speed, {opacity: 0});
  }

  private hideCursors() {
    TweenMax.to(this.cursors, 0.25, {autoAlpha: 0});
  }

  private showCursors() {
    TweenMax.to(this.cursors, 0.25, {autoAlpha: 1});
  }


  public async zoom(speed) {
    this.zoomed = true;
    this.disableScroll();
    this.disableDrag();
    this.disableClick();
    this.hideDescriptions();
    this.hideCursors();
    this.moveSideItemsOut(speed);
    this.getCurrentItem().zoom(speed);
  }

  public zoomOut(speed) {
    this.zoomed = false;
    this.moveSideItemsIn(speed);
    this.getCurrentItem().zoomOut(speed);
    this.enableScroll();
    this.enableClick();
    this.showDescriptions();
    this.enableDrag();
  }

  public next() {
    if (this.currentIndex == this.carouselItems.length - 1) {
      return;
    }
    this.setCurrentItem(this.currentIndex + 1);
    const x = this.getX() - this.getSlideWidth();
    this.slideCarouselTo(x);
  }

  public previous() {
    if (this.currentIndex == 0) {
      return;
    }
    this.setCurrentItem(this.currentIndex - 1);
    const x = this.getX() + this.getSlideWidth();
    this.slideCarouselTo(x);
  }

  private slideCarouselTo(x) {
    this.tickDescription();
    this.disableScroll();
    this.disableRollovers();
    this.getCurrentItem().hideRolloverVideo();

    TweenMax.to(this.carouselItemContainer, 1, {
      x: x,
      ease: Power4.easeOut,
      onUpdate: () => {
        this.update3dEffect();
      },
      onComplete: () => {
        this.enableRollovers();
        this.enableScroll();
      }
    });
  }

  public gotoSlideById(id, speed) {
    this.disableScroll();
    return new Promise(resolve => {
      const slide = this.getSlideById(id);
      if (this.currentIndex == slide.index) {
        resolve();
        return;
      }
      this.setCurrentItem(slide.index);
      TweenMax.to(this.carouselItemContainer, speed, {
        x: -slide.element.offsetLeft,
        ease: Power4.easeOut,
        onUpdate: () => {
          this.update3dEffect();
        },
        onComplete: () => {
          this.enableScroll();
          resolve();
        }
      });
      this.tickDescription();
    });
  }

  private readMoreHandler = e => {
    e.preventDefault();
    this.emit(EVENTS.select, {storyId: this.getCurrentItem().id});
  };

  private tickDescription() {
    const activeDescription = this.descriptionsTicker.querySelector('.active');
    if (activeDescription) {
      activeDescription.classList.remove('active');
      TweenMax.to(activeDescription, 0.15, {opacity: 0});
    }
    const description = this.descriptionsTicker.querySelectorAll('.description')[this.currentIndex] as HTMLElement;
    description.classList.add('active');
    TweenMax.to(description, 0.15, {opacity: 1});
    this.updateDescription(1);
  }

  private updateDescription(speed) {
    const activeDescription = this.descriptionsTicker.querySelector('.active') as HTMLElement;
    if (activeDescription) {
      TweenMax.to(this.descriptionScroller, 1, {ease: Power4.easeOut, y: -activeDescription.offsetTop});
    }
  }

  public presetAnimation() {
    TweenMax.set([this.element, this.descriptionsTicker], {opacity: 0});
    TweenMax.set(this.carouselDockedInfo, {opacity: 0, y: -10});
  }

  public animateIn(delay = 0) {
    this.element.style.display = `block`;
    this.showSlides();
    this.resize();
    this.update3dEffect();
    return new Promise(resolve => {
      TweenMax.to(this.descriptionsTicker, 0.4, {opacity: 1});
      TweenMax.to(this.carouselDockedInfo, 0.4, {opacity: 1, y: 0});
      TweenMax.to(this.element, 0.4, {
        opacity: 1,
        delay,
        onComplete: () => {
          this.enableScroll();
          resolve();
        }
      });
    });
  }

  public show() {
    this.element.style.display = `block`;
    this.resize();
    TweenMax.set([this.element, this.descriptionsTicker], {opacity: 1});
    TweenMax.set(this.carouselDockedInfo, {opacity: 1, y: 0});
  }

  public disableDrag() {
    this.dragEnabled = false;
    this.draggables[0].disable();
    this.draggables[0].target.onclick = this.draggables[0].vars.onClick;
  }

  public enableDrag() {
    if (device.isTouch()) {
      this.dragEnabled = true;
      this.draggables[0].enable();
    }
  }

  public disableScroll() {
    this.scrollEnabled = false;
  }

  public enableScroll() {
    if (!this.zoomed) {
      this.scrollEnabled = true;
    }
  }

  public disableClick() {
    this.draggables[0].target.onclick = null;
  }

  public enableClick() {
    this.draggables[0].target.onclick = this.draggables[0].vars.onClick;
  }

  public hideOtherSlides() {
    this.showSlides();
    for (var i = 0; i < this.carouselItems.length; i++) {
      if (this.currentIndex != i) {
        this.carouselItems[i].hide();
      }
    }
  }

  public showSlides() {
    for (var i = 0; i < this.carouselItems.length; i++) {
        this.carouselItems[i].show();
    }
  }
}
